import { LOW_QUALITY, parseCatalogTitle } from "../home/catalogTitleMatch.js";
import { isForeignCategory } from "../home/shelfFill.js";
import type { CatalogItem } from "./catalogQuery.js";

/**
 * One entry per film or series in browsing lists (owner, 2026-10-13: "we need to clean up the
 * multiple entries in movies. it isn't a clean look", and "make sure not to hide movies or series
 * that are actually different").
 *
 * A provider lists the same film under several streams: 44,218 film entries for 37,442 films on
 * the owner's panel. The catalog carries no quality field, so which stream is best is judged from
 * what is there (see streamQuality), and the other streams are kept as fallbacks for when the
 * chosen one will not play.
 *
 * What counts as the SAME title is deliberately narrow, so nothing different is ever hidden:
 *  - the provider gave it a TMDB id (an entry without one is never merged: "Love Island" the UK
 *    and the US series would both read the same without it);
 *  - the same normalised title, the same year, the same TMDB id. "Karate Kid" and "Karate Kid
 *    Part II" share an id on some panels and stay two; "Blood In Blood Out" is not "Bound by
 *    Honor"; a remake in another year is another film.
 *
 * Two flags per entry, as the APK's norm table holds them (CatalogStore.markDuplicates):
 *  - the best of its title overall, for "All genres", search and Home;
 *  - the best within its own category, so a category list still shows a film whose overall best
 *    stream sits in another category (a French dub is in the French category).
 * A lookup by id (My List, history) and the fallbacks of one title see every copy.
 */

/** The key that makes entries one title, or null when this entry is never merged. */
export function dupKeyOf(item: CatalogItem): string | null {
  if (!(typeof item.tmdbId === "number" && item.tmdbId > 0)) return null;
  const parsed = parseCatalogTitle(item.rawTitle);
  if (!parsed.key) return null;
  const year = "year" in item && typeof item.year === "number" ? item.year : parsed.year;
  return `${parsed.key}|${year ?? ""}|${item.tmdbId}`;
}

const FOUR_K = /\b(4k|uhd|2160p?)\b/;
const FULL_HD = /\b(fhd|1080p?|full ?hd)\b/;
const HD = /\b(hd|720p?)\b/;
const SD = /\b(sd|480p?)\b/;

/** The container the stream address ends in; a redacted address keeps its extension. */
function extensionOf(url: string | undefined): string {
  const match = /\.([a-z0-9]{2,4})(?:[?#]|$)/i.exec(url ?? "");
  return match ? match[1]!.toLowerCase() : "";
}

/**
 * How good this stream looks, higher is better. Only what the catalog says: a camera recording is
 * the worst, a foreign-language category is below an English one, 4K above full HD above HD above
 * SD (from the name or the category), and a container ExoPlayer plays without fuss (mp4, then
 * mkv) breaks a tie. Equal scores keep the provider's own order.
 */
export function streamQuality(item: CatalogItem): number {
  let score = 0;
  if (LOW_QUALITY.test(item.rawTitle)) score -= 100;
  const category = (item.categoryId ?? "").toLowerCase();
  if (category && isForeignCategory(category)) score -= 50;
  const text = `${item.rawTitle.toLowerCase()} ${category}`;
  if (FOUR_K.test(text)) score += 30;
  else if (FULL_HD.test(text)) score += 20;
  else if (HD.test(text)) score += 10;
  else if (SD.test(text)) score -= 5;
  const ext = extensionOf("streamUrl" in item ? item.streamUrl : undefined);
  if (ext === "mp4") score += 3;
  else if (ext === "mkv") score += 2;
  else if (ext === "m3u8") score += 1;
  else if (ext === "avi") score -= 5;
  return score;
}

export interface DuplicateFlags {
  /** Ids that are NOT the best stream of their title overall. */
  hiddenAll: ReadonlySet<string>;
  /** Ids that are NOT the best stream of their title within their category. */
  hiddenCat: ReadonlySet<string>;
}

const cache = new WeakMap<readonly CatalogItem[], DuplicateFlags>();

/** A stable ordering: better quality first, then the provider's own order (the position). */
function better(aQuality: number, aPos: number, bQuality: number, bPos: number): boolean {
  return aQuality !== bQuality ? aQuality > bQuality : aPos < bPos;
}

export function duplicateFlags(items: readonly CatalogItem[]): DuplicateFlags {
  const held = cache.get(items);
  if (held) return held;
  // key -> the best so far overall; key|category -> the best so far within the category.
  const bestAll = new Map<string, { id: string; quality: number; pos: number }>();
  const bestCat = new Map<string, { id: string; quality: number; pos: number }>();
  const keys: Array<string | null> = new Array(items.length);
  const qualities: number[] = new Array(items.length);
  items.forEach((item, pos) => {
    const key = dupKeyOf(item);
    keys[pos] = key;
    if (key === null) return;
    const quality = streamQuality(item);
    qualities[pos] = quality;
    const all = bestAll.get(key);
    if (!all || better(quality, pos, all.quality, all.pos)) bestAll.set(key, { id: item.id, quality, pos });
    const catKey = `${key}|${item.categoryId ?? ""}`;
    const cat = bestCat.get(catKey);
    if (!cat || better(quality, pos, cat.quality, cat.pos)) bestCat.set(catKey, { id: item.id, quality, pos });
  });
  const hiddenAll = new Set<string>();
  const hiddenCat = new Set<string>();
  items.forEach((item, pos) => {
    const key = keys[pos];
    if (key === null || key === undefined) return;
    if (bestAll.get(key)!.id !== item.id) hiddenAll.add(item.id);
    if (bestCat.get(`${key}|${item.categoryId ?? ""}`)!.id !== item.id) hiddenCat.add(item.id);
  });
  const flags = { hiddenAll, hiddenCat };
  cache.set(items, flags);
  return flags;
}

/** The catalog with one entry per title: the best stream of each, in the provider's order. */
export function primaryItems<T extends CatalogItem>(items: readonly T[]): T[] {
  const { hiddenAll } = duplicateFlags(items);
  return hiddenAll.size === 0 ? (items as T[]) : items.filter((item) => !hiddenAll.has(item.id));
}

/** The other streams of the same title, best first: what plays when the chosen one will not. */
export function alternatesOf<T extends CatalogItem>(items: readonly T[], item: CatalogItem): T[] {
  const key = dupKeyOf(item);
  if (key === null) return [];
  const same: Array<{ item: T; quality: number; pos: number }> = [];
  items.forEach((other, pos) => {
    if (other.id !== item.id && dupKeyOf(other) === key) same.push({ item: other, quality: streamQuality(other), pos });
  });
  same.sort((a, b) => (a.quality !== b.quality ? b.quality - a.quality : a.pos - b.pos));
  return same.map((entry) => entry.item);
}
